import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/cn";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ToastVariant = "default" | "success" | "error" | "warning" | "info";

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
}

interface ToastContextValue {
  toast: (message: string, opts?: { variant?: ToastVariant; duration?: number }) => void;
  dismiss: (id: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

let _id = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (
      message: string,
      opts?: { variant?: ToastVariant; duration?: number },
    ) => {
      const id = String(++_id);
      const variant = opts?.variant ?? "default";
      const duration = opts?.duration ?? 4000;

      setToasts((prev) => [...prev, { id, message, variant, duration }]);

      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}

      {/* Toast container — bottom-right */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Toast Card                                                         */
/* ------------------------------------------------------------------ */

const variantStyles: Record<ToastVariant, string> = {
  default: "border-white/10 bg-surface-raised text-white",
  success: "border-emerald-500/30 bg-emerald-950/80 text-emerald-200",
  error: "border-danger/30 bg-red-950/80 text-red-200",
  warning: "border-amber-500/30 bg-amber-950/80 text-amber-200",
  info: "border-primary/30 bg-blue-950/80 text-blue-200",
};

const variantIcons: Record<ToastVariant, ReactNode> = {
  default: null,
  success: <CheckCircle className="h-4 w-4 shrink-0 text-emerald-400" />,
  error: <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />,
  warning: <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />,
  info: <Info className="h-4 w-4 shrink-0 text-blue-400" />,
};

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: () => void;
}) {
  return (
    <div
      className={cn(
        "pointer-events-auto flex items-center gap-2 rounded-lg border px-4 py-3 shadow-card animate-[slide-up_200ms_ease-out]",
        variantStyles[toast.variant],
      )}
      role="alert"
    >
      {variantIcons[toast.variant]}
      <span className="text-sm">{toast.message}</span>
      <button
        onClick={onDismiss}
        className="ml-2 rounded p-0.5 transition-colors hover:bg-white/10"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
